import { Box, ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import React, { useMemo } from 'react';
import { ContainerWrapper, Thumbnail, FeaturedPopularTourWrapper } from './ThingToDo.styles';
import { pxToRem } from '@shared/joykit/packages/core/common';
import { Link } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { truncateString } from './utils';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import {
  hed6ToSubHeadingOverride,
  body1ToSubHeadingOverride,
  hed1ToSubHeadingOverride,
} from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { ThingToDoComponentProps, useThingToDo } from '.';
import { useThingsToDoTelemetry } from './ThingsToDo.telemetry';
import { useColorPaletteProvider } from '@apps/guest/packages/layout-engine/layouts/ColorPaletteProvider/ColorPaletteProvider';
import { getButtonStylesWithAccentColor } from '@apps/guest/packages/layout-engine/layouts/layout.utils';

export const ThingToDoSidebar: React.FC<ThingToDoComponentProps> = ({ designType = 'fullRow', ...props }: ThingToDoComponentProps) => {
  const { t } = useTranslation('guestSiteCustom');
  const telemetry = useThingsToDoTelemetry();
  const { constructCampaignUrl } = useThingToDo();
  const { accentColor } = useColorPaletteProvider();
  const buttonStyles = useMemo(() => (accentColor ? getButtonStylesWithAccentColor(accentColor) : {}), [accentColor]);

  return (
    <ContainerWrapper
      flexDirection={designType === 'fullRow' ? ['column', 'column', 'row', 'row'] : 'column'}
      as="section"
      alignItems={designType === 'fullRow' ? ['center', 'center', 'stretch', 'stretch'] : 'center'}
    >
      {/* eslint-disable-next-line react/jsx-no-target-blank */}
      <a
        href={constructCampaignUrl(props.url)}
        target="_blank"
        rel="noopener sponsored nofollow"
        referrerPolicy="origin"
        onClick={() => {
          telemetry.thingsToDoCTAClicked(props.url, {
            componentName: 'ThingToDoSidebar',
            url: props.url,
            thingToDoName: props.title,
            city: props.location,
            tours: props.tours,
          });
        }}
      >
        <Box
          maxWidth={['100%', pxToRem(440)]}
          flex="1"
          width="100%"
          cursor="pointer"
        >
          {props.image?.url && (
            <Box
              as="figure"
              width="100%"
              flex="1"
              borderRadius={4}
              overflow="hidden"
              height={pxToRem(216)}
            >
              <picture>
                <source srcSet={props.image.url} />
                <Thumbnail
                  src={props.image.url}
                  alt={props.title}
                  height={pxToRem(216)}
                />
              </picture>
            </Box>
          )}
          <Flex
            flexDirection="column"
            paddingBottom={[0, pxToRem(24)]}
            paddingTop={pxToRem(24)}
            gap={pxToRem(16)}
          >
            <GuestSiteTypographyOverride override={hed6ToSubHeadingOverride}>
              <TextV2
                as="h2"
                typographyVariant="hed6"
              >
                {props.title}
              </TextV2>
            </GuestSiteTypographyOverride>
            <GuestSiteTypographyOverride override={body1ToSubHeadingOverride}>
              <TextV2 typographyVariant="body1">{props.description}</TextV2>
            </GuestSiteTypographyOverride>
          </Flex>
        </Box>
      </a>
      <Box
        maxWidth={['100%', pxToRem(352)]}
        as="aside"
        flex="1"
        width="100%"
      >
        <GuestSiteTypographyOverride override={hed1ToSubHeadingOverride}>
          <TextV2
            as="h3"
            typographyVariant="hed1"
          >
            {t('thingsToDo', 'popularNearby')()}
          </TextV2>
        </GuestSiteTypographyOverride>
        {props.tours?.map((tour, index) => (
          <FeaturedPopularTour
            isLast={props.tours?.length === index + 1}
            key={tour.id}
            id={tour.id}
            title={tour.title}
            description={tour.description ?? ''}
            url={constructCampaignUrl(tour.url)}
            image={tour.image}
          />
        ))}
        <Box paddingY={pxToRem(16)}>
          <ButtonV2
            as="a"
            href={props.url}
            target="_blank"
            rel="noopener sponsored nofollow"
            referrerPolicy="origin"
            onClick={() => {
              telemetry.thingsToDoCTAClicked(props.url, {
                componentName: 'thingsToDoV1Sidebar',
                url: props.url,
                thingToDoName: props.title,
                city: props.location,
                tours: props.tours,
              });
            }}
            endIcon={<Link />}
            fontFamily="Inter UI"
            variant="outline"
            fontWeight={600}
            intent="neutral"
            shape="rounded"
            fullWidth
            {...buttonStyles}
          >
            {t('thingsToDo', 'seeMore')()}
          </ButtonV2>
        </Box>
      </Box>
    </ContainerWrapper>
  );
};

interface PopularTourProps {
  id: string;
  title: string;
  description: string;
  url: string;
  image?: { id: string; url: string };
  isLast?: boolean;
  location?: string;
}
const FeaturedPopularTour: React.FC<PopularTourProps> = props => {
  const telemetry = useThingsToDoTelemetry();
  return (
    <FeaturedPopularTourWrapper
      isLast={props.isLast ?? false}
      as="a"
      href={props.url}
      target="_blank"
      rel="noopener sponsored nofollow"
      referrerPolicy="origin"
      onClick={() => {
        telemetry.thingsToDoTourClicked(props.url, {
          componentName: 'DefaultThingsToDo',
          version: 'v1',
          url: props.url,
          thingToDoName: props.title,
          city: props.location,
        });
      }}
    >
      {props.image && (
        <Box
          width={pxToRem(64)}
          height={pxToRem(64)}
          as="figure"
          objectFit="cover"
          borderRadius={2}
          overflow="hidden"
        >
          <picture>
            <source srcSet={props.image.url} />
            <Thumbnail
              src={props.image.url}
              alt={props.title}
              height={pxToRem(64)}
            />
          </picture>
        </Box>
      )}
      <Box flex="1">
        <TextV2
          as="h3"
          typographyVariant="hed1"
          fontFamily="Inter UI"
          fontWeight={600}
          fontSize="0.9375rem"
          lineHeight="1.4"
          letterSpacing="0em"
        >
          {props.title}
        </TextV2>
        <TextV2
          as="p"
          title={props.description}
          typographyVariant="label2"
          truncate
          marginTop={pxToRem(4)}
          fontFamily="Inter UI"
          fontSize="12px"
          fontWeight={400}
          lineHeight="140%"
        >
          {truncateString(props.description)}
        </TextV2>
      </Box>
    </FeaturedPopularTourWrapper>
  );
};
